// Google Tag Manager Utility Functions

/**
 * Check if GTM is properly loaded
 * @returns {boolean} - True if GTM is loaded
 */
export const isGTMLoaded = () => {
  const hasDataLayer = typeof window !== 'undefined' && window.dataLayer;
  const hasGTM = typeof window !== 'undefined' && window.google_tag_manager;
  console.log('GTM Status:', {
    hasDataLayer: !!hasDataLayer,
    hasGTM: !!hasGTM,
    dataLayerLength: hasDataLayer ? window.dataLayer.length : 0
  });
  return hasDataLayer;
};

/**
 * Push events to GTM dataLayer
 * @param {Object} eventData - Event data to push
 */
export const gtmPush = (eventData) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(eventData);
    console.log('GTM Event pushed:', eventData);
    
    // Also log current dataLayer state for debugging
    console.log('Current dataLayer:', window.dataLayer);
  } else {
    console.warn('GTM dataLayer not available');
    console.log('Window object:', typeof window);
    console.log('DataLayer exists:', !!(typeof window !== 'undefined' && window.dataLayer));
    
    // Try to check if GTM script has loaded
    const gtmScripts = document.querySelectorAll('script[src*="googletagmanager.com"]');
    console.log('GTM scripts found:', gtmScripts.length);
  }
};

/**
 * Track purchase event in GTM
 * @param {Object} orderData - Order data
 */
export const gtmTrackPurchase = (orderData) => {
  // gtmPush({
  //   event: 'purchase',
  //   ecommerce: {
  //     transaction_id: orderData.order_id || orderData.id,
  //     value: orderData.payment?.amount || orderData.total_amount || 0,
  //     currency: orderData.details?.currency_type || orderData.currency_type || 'INR',
  //     items: (orderData.new_ordered_items || orderData.items || []).map(item => ({
  //       item_id: item.subscription_plan?.subscription_plan_id || item.id,
  //       item_name: item.subscription_plan?.plan_name || item.name,
  //       item_category: item.subscription_plan?.service_name || 'subscription',
  //       price: item.cost || item.price || 0,
  //       quantity: item.quantity || 1
  //     }))
  //   },
  //   // Additional custom parameters
  //   order_id: orderData.order_id || orderData.id,
  //   payment_method: orderData.payment?.payment_method,
  //   discount_amount: orderData.amount_saved_dict?.amount_saved || 0,
  //   fleek_coins_used: orderData.details?.coin_value_used || 0
  // });
};

/**
 * Track begin checkout event in GTM
 * @param {Object} cartData - Cart data
 */
export const gtmTrackBeginCheckout = (cartData) => {
  gtmPush({
    event: 'begin_checkout',
    ecommerce: {
      currency: cartData.currency_type || 'INR',
      value: cartData.total_amount || cartData.final_payment_amount?.no_coin_and_no_money || 0,
      items: (cartData.items || [cartData]).map(item => ({
        item_id: item.subscription_plan?.subscription_plan_id || item.id,
        item_name: item.subscription_plan?.plan_name || item.name,
        item_category: item.subscription_plan?.service_name || 'subscription',
        price: item.cost || item.price || 0,
        quantity: item.quantity || 1
      }))
    }
  });
};

/**
 * Track add to cart event in GTM
 * @param {Object} product - Product data
 */
export const gtmTrackAddToCart = (product) => {
  gtmPush({
    event: 'add_to_cart',
    ecommerce: {
      currency: product.currency_type || 'INR',
      value: product.cost || product.price || 0,
      items: [{
        item_id: product.subscription_plan_id || product.id,
        item_name: product.plan_name || product.name,
        item_category: product.service_name || 'subscription',
        price: product.cost || product.price || 0,
        quantity: 1
      }]
    }
  });
};

/**
 * Track view item event in GTM
 * @param {Object} product - Product data
 */
export const gtmTrackViewItem = (product) => {
  gtmPush({
    event: 'view_item',
    ecommerce: {
      currency: product.currency_type || 'INR',
      value: product.cost || product.price || 0,
      items: [{
        item_id: product.subscription_plan_id || product.id,
        item_name: product.plan_name || product.name,
        item_category: product.service_name || 'subscription',
        price: product.cost || product.price || 0,
        quantity: 1
      }]
    }
  });
};

/**
 * Track custom event in GTM
 * @param {string} eventName - Event name
 * @param {Object} parameters - Event parameters
 */
export const gtmTrackCustomEvent = (eventName, parameters = {}) => {
  gtmPush({
    event: eventName,
    ...parameters
  });
};

/**
 * Track page view in GTM
 * @param {string} pageTitle - Page title
 * @param {string} pagePath - Page path
 */
export const gtmTrackPageView = (pageTitle, pagePath) => {
  gtmPush({
    event: 'page_view',
    page_title: pageTitle,
    page_location: window.location.href,
    page_path: pagePath || window.location.pathname
  });
};

/**
 * Track user login in GTM
 * @param {string} method - Login method
 */
export const gtmTrackLogin = (method = 'phone_otp') => {
  gtmPush({
    event: 'login',
    method: method
  });
};

/**
 * Track user signup in GTM
 * @param {string} method - Signup method
 */
export const gtmTrackSignUp = (method = 'phone_otp') => {
  gtmPush({
    event: 'sign_up',
    method: method
  });
};

/**
 * Track payment failure in GTM
 * @param {Object} paymentData - Payment data
 */
export const gtmTrackPaymentFailure = (paymentData) => {
  gtmPush({
    event: 'payment_failed',
    order_id: paymentData.order_id,
    payment_id: paymentData.payment_id,
    failure_reason: paymentData.error || 'unknown',
    gateway: paymentData.gateway,
    value: paymentData.amount || 0,
    currency: paymentData.currency || 'INR'
  });
};

// Initialize GTM helper functions
export const GTM = {
  push: gtmPush,
  isLoaded: isGTMLoaded,
  trackPurchase: gtmTrackPurchase,
  trackBeginCheckout: gtmTrackBeginCheckout,
  trackAddToCart: gtmTrackAddToCart,
  trackViewItem: gtmTrackViewItem,
  trackCustomEvent: gtmTrackCustomEvent,
  trackPageView: gtmTrackPageView,
  trackLogin: gtmTrackLogin,
  trackSignUp: gtmTrackSignUp,
  trackPaymentFailure: gtmTrackPaymentFailure
};

export default GTM;
